import { useEffect, useMemo, useState, type ReactNode } from "react";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { auditListResponseSchema, type AuditListResponse, type AuditViewRow } from "@amber/contracts";
import { auditKeys } from "@amber/access/ui";
import {
  AppLink,
  Button,
  ColumnsMenu,
  DataTable,
  Drawer,
  FilterChip,
  LinkButton,
  POLL_MS,
  SectionHeader,
  SegmentedFilter,
  Select,
  Tag,
  TextInput,
  Toolbar,
  ToolbarSpacer,
  apiGet,
  dataColumns,
  formatDateTime,
  isApiError,
  useColumnVisibility,
  withQuery,
  type ColumnToggle,
  type DataColumn,
} from "@amber/ui";
import type { AuditRange, AuditSearch } from "./search";

const PAGE_SIZE = 50;
const RANGES = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
] as const satisfies readonly { value: AuditRange; label: string }[];

// `subject` holds a subject type ("profile") or one record's ID ("prf_01…"); an ID filters on that record.
const SUBJECT_ID = /^[a-z]{3}_[0-9A-Z]{26}$/;

function subjectRecord(search: AuditSearch): string | undefined {
  return search.subject && SUBJECT_ID.test(search.subject) ? search.subject : undefined;
}

function auditQuery(search: AuditSearch) {
  const record = subjectRecord(search);
  const params = {
    actor: search.actor,
    action_type: search.action,
    subject: record ? undefined : search.subject,
    subject_id: record,
    range: search.range === "all" ? undefined : search.range,
    q: search.q,
    limit: String(PAGE_SIZE),
  };
  return infiniteQueryOptions({
    queryKey: auditKeys.list(params),
    queryFn: ({ pageParam, signal }) =>
      apiGet(withQuery("/api/audit", { ...params, cursor: pageParam ?? undefined }), auditListResponseSchema, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });
}

function show(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

export function changeSummary(entry: Pick<AuditViewRow, "before" | "after">): string {
  const before = entry.before ?? {};
  const after = entry.after ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  const changed = keys.filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]));
  if (changed.length === 0) return "—";
  return changed.map((k) => `${k.replace(/_/g, " ")}: ${show(before[k])} → ${show(after[k])}`).join(" · ");
}

const PINNED_CELL =
  "sticky left-0 z-pinned-cell shadow-[inset_-1px_0_0_var(--color-divider)] group-data-[selected]/row:shadow-[inset_3px_0_0_var(--color-link),inset_-1px_0_0_var(--color-divider)]";

const AUDIT_COLUMNS = [
  { id: "time", label: "Time", canHide: false },
  { id: "type", label: "Action type", hiddenByDefault: true },
] as const;

const col = dataColumns<AuditViewRow>();

function auditColumns(typeName: (key: string) => string | undefined): DataColumn<AuditViewRow>[] {
  return [
    col.display({
      id: "time",
      header: "Time",
      meta: { width: 130, cellClassName: `${PINNED_CELL} tabular` },
      cell: ({ row }) => formatDateTime(row.original.at),
    }),
    col.display({
      id: "actor",
      header: "Actor",
      meta: { width: 150, cellClassName: "text-fg" },
      cell: ({ row }) => row.original.actor.name,
    }),
    col.display({
      id: "action",
      header: "Action",
      meta: { width: 190, cellClassName: "text-fg" },
      cell: ({ row }) => row.original.action_label,
    }),
    col.display({
      id: "type",
      header: "Action type",
      meta: { width: 150 },
      cell: ({ row }) => typeName(row.original.action_type),
    }),
    col.display({
      id: "subject",
      header: "Subject",
      meta: { width: 200 },
      cell: ({ row: { original: row } }) =>
        row.redacted ? <Tag tone="quiet">Restricted record</Tag> : (row.subject?.name ?? "—"),
    }),
    col.display({
      id: "reason",
      header: "Reason",
      meta: { width: 200 },
      cell: ({ row: { original: row } }) => (row.redacted ? "—" : (row.reason ?? "—")),
    }),
    col.display({
      id: "change",
      header: "Before → after",
      meta: { width: 240, cellClassName: "font-mono text-sm" },
      cell: ({ row: { original: row } }) => (row.redacted ? "—" : changeSummary(row)),
    }),
  ];
}

type Facets = AuditListResponse["facets"];
type SetSearch = (patch: Partial<AuditSearch>) => void;

function nameOf(list: readonly { key: string; name?: string; label?: string }[] | undefined, key: string | undefined) {
  return list?.find((x) => x.key === key)?.name ?? list?.find((x) => x.key === key)?.label ?? key;
}

function AuditToolbar({
  search,
  facets,
  text,
  onText,
  set,
  toggles,
}: {
  search: AuditSearch;
  facets: Facets | undefined;
  text: string;
  onText: (text: string) => void;
  set: SetSearch;
  toggles: readonly ColumnToggle[];
}) {
  return (
    <Toolbar>
      <label className="sr-only" htmlFor="audit-actor">
        Actor
      </label>
      <Select
        id="audit-actor"
        style={{ width: 170 }}
        value={search.actor ?? ""}
        onChange={(e) => {
          set({ actor: e.target.value || undefined });
        }}
      >
        <option value="">All actors</option>
        {facets?.actors.map((a) => (
          <option key={a.key} value={a.key}>
            {a.name}
          </option>
        ))}
      </Select>
      <label className="sr-only" htmlFor="audit-action">
        Action type
      </label>
      <Select
        id="audit-action"
        style={{ width: 180 }}
        value={search.action ?? ""}
        onChange={(e) => {
          set({ action: e.target.value || undefined });
        }}
      >
        <option value="">All action types</option>
        {facets?.action_types.map((a) => (
          <option key={a.key} value={a.key}>
            {a.label}
          </option>
        ))}
      </Select>
      <label className="sr-only" htmlFor="audit-subject">
        Subject type
      </label>
      <Select
        id="audit-subject"
        style={{ width: 160 }}
        value={subjectRecord(search) ? "" : (search.subject ?? "")}
        onChange={(e) => {
          set({ subject: e.target.value || undefined });
        }}
      >
        <option value="">All subjects</option>
        {facets?.subject_kinds.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </Select>
      <SegmentedFilter<AuditRange>
        label="Date range"
        value={search.range ?? "all"}
        options={RANGES}
        onChange={(range) => {
          set({ range: range === "all" ? undefined : range });
        }}
      />
      <label className="sr-only" htmlFor="audit-q">
        Search the audit log
      </label>
      <TextInput
        id="audit-q"
        type="search"
        placeholder="Search reasons, actions, subjects"
        style={{ width: 220 }}
        value={text}
        onChange={(e) => {
          onText(e.target.value);
        }}
      />
      <ToolbarSpacer />
      <ColumnsMenu columns={toggles} />
    </Toolbar>
  );
}

function AuditChips({
  search,
  facets,
  subjectName,
  set,
  onClearText,
  onReset,
}: {
  search: AuditSearch;
  facets: Facets | undefined;
  subjectName: string | undefined;
  set: SetSearch;
  onClearText: () => void;
  onReset: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 pb-2 max-compact:px-2">
      {search.actor ? (
        <FilterChip
          label="Actor"
          value={nameOf(facets?.actors, search.actor)}
          onRemove={() => {
            set({ actor: undefined });
          }}
        />
      ) : null}
      {search.action ? (
        <FilterChip
          label="Action type"
          value={nameOf(facets?.action_types, search.action)}
          onRemove={() => {
            set({ action: undefined });
          }}
        />
      ) : null}
      {search.subject ? (
        <FilterChip
          label="Subject"
          value={
            subjectRecord(search) ? (subjectName ?? search.subject) : nameOf(facets?.subject_kinds, search.subject)
          }
          onRemove={() => {
            set({ subject: undefined });
          }}
        />
      ) : null}
      {search.range && search.range !== "all" ? (
        <FilterChip
          label="Date"
          value={RANGES.find((r) => r.value === search.range)?.label}
          onRemove={() => {
            set({ range: undefined });
          }}
        />
      ) : null}
      {search.q ? (
        <FilterChip
          label="Text"
          value={`“${search.q}”`}
          onRemove={() => {
            onClearText();
            set({ q: undefined });
          }}
        />
      ) : null}
      <LinkButton onClick={onReset}>Reset all filters</LinkButton>
    </div>
  );
}

function isFiltered(search: AuditSearch): boolean {
  return (
    Boolean(search.actor ?? search.action ?? search.subject ?? search.q) ||
    (search.range !== undefined && search.range !== "all")
  );
}

function EntryCount({ total, filtered }: { total: number; filtered: boolean }) {
  return (
    <span>
      {total === 1 ? "1 entry" : `${String(total)} entries`}
      {filtered ? " match these filters" : ""}
    </span>
  );
}

function LoadMore({ busy, onLoad }: { busy: boolean; onLoad: () => void }) {
  return (
    <div className="flex justify-center border-t border-divider p-2">
      <Button size="sm" aria-busy={busy || undefined} onClick={onLoad}>
        Load more
      </Button>
    </div>
  );
}

function noEntries(loaded: boolean, filtered: boolean, onReset: () => void): ReactNode {
  if (!loaded) return null;
  if (!filtered) return "No manual actions yet";
  return (
    <>
      No audit entries match these filters. <LinkButton onClick={onReset}>Reset all filters</LinkButton>
    </>
  );
}

export function AuditLog({ search, onSearch }: { search: AuditSearch; onSearch: (next: AuditSearch) => void }) {
  const query = useInfiniteQuery(auditQuery(search));
  const visibility = useColumnVisibility(AUDIT_COLUMNS);
  const showType = visibility.isVisible("type");
  const [text, setText] = useState(search.q ?? "");
  const first = query.data?.pages[0];
  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];
  const selected = rows.find((r) => r.id === search.entry);
  const filtered = isFiltered(search);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = text.trim() || undefined;
      if (q !== search.q) onSearch({ ...search, q, entry: undefined });
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [text, search, onSearch]);

  const set = (patch: Partial<AuditSearch>) => {
    onSearch({ ...search, ...patch, entry: undefined });
  };
  const open = (row: AuditViewRow) => {
    onSearch({ ...search, entry: row.id });
  };
  const actionTypes = first?.facets.action_types;
  const columns = useMemo(
    () => auditColumns((key) => actionTypes?.find((x) => x.key === key)?.label ?? key),
    [actionTypes],
  );
  const resetAll = () => {
    setText("");
    onSearch({});
  };

  return (
    <div className="flex min-h-0 flex-1">
      <section aria-labelledby="section-title" className="flex min-h-0 min-w-0 flex-1 flex-col">
        <SectionHeader
          titleId="section-title"
          title="Audit log"
          lead="Every manual action in the workspace: who did what, when, to which record, why, and what changed. Entries can't be edited or removed."
          meta={first ? <EntryCount total={first.total} filtered={filtered} /> : null}
        />
        <AuditToolbar
          search={search}
          facets={first?.facets}
          text={text}
          onText={setText}
          set={set}
          toggles={visibility.toggles}
        />
        {filtered ? (
          <AuditChips
            search={search}
            facets={first?.facets}
            subjectName={rows.find((r) => r.subject?.id === search.subject)?.subject?.name}
            set={set}
            onClearText={() => {
              setText("");
            }}
            onReset={resetAll}
          />
        ) : null}
        <DataTable
          label="Audit entries"
          columns={columns}
          rows={rows}
          rowId={(row) => row.id}
          minWidth={showType ? 1180 : 1040}
          hidden={visibility.hidden}
          selectedId={search.entry ?? null}
          rowOptions={(row) => ({
            ariaSelected: row.id === search.entry,
            onOpen: () => {
              open(row);
            },
            onPick: () => {
              open(row);
            },
          })}
          isPending={query.isPending}
          busy={query.isFetching && !query.isFetchingNextPage}
          empty={
            query.isError ? (
              <>
                Couldn&apos;t load the audit log. {isApiError(query.error) ? query.error.message : null}{" "}
                <LinkButton
                  onClick={() => {
                    void query.refetch();
                  }}
                >
                  Retry
                </LinkButton>
              </>
            ) : (
              noEntries(query.data !== undefined, filtered, resetAll)
            )
          }
          after={
            query.hasNextPage ? (
              <LoadMore
                busy={query.isFetchingNextPage}
                onLoad={() => {
                  void query.fetchNextPage();
                }}
              />
            ) : null
          }
        />
      </section>
      {selected ? (
        <Drawer
          title={selected.action_label}
          subtitle={`${selected.actor.name} · ${formatDateTime(selected.at)}`}
          onClose={() => {
            onSearch({ ...search, entry: undefined });
          }}
        >
          <AuditEntryDetail row={selected} />
        </Drawer>
      ) : null}
    </div>
  );
}

function AuditSubject({ row }: { row: AuditViewRow }) {
  if (row.redacted) return "Restricted record";
  if (!row.subject) return "—";
  return row.subject.href ? <AppLink href={row.subject.href}>{row.subject.name}</AppLink> : row.subject.name;
}

function AuditEntryDetail({ row }: { row: AuditViewRow }) {
  const before = row.before ?? {};
  const after = row.after ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return (
    <dl className="m-0 grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-ui [&_dd]:m-0 [&_dd]:min-w-0 [&_dd]:break-words [&_dt]:text-muted">
      <dt>Action</dt>
      <dd>{row.action_label}</dd>
      <dt>Actor</dt>
      <dd>{row.actor.name}</dd>
      <dt>Time</dt>
      <dd>{formatDateTime(row.at)}</dd>
      <dt>Subject</dt>
      <dd>
        <AuditSubject row={row} />
      </dd>
      <dt>Reason</dt>
      <dd>{row.redacted ? "Hidden for your role" : (row.reason ?? "—")}</dd>
      {!row.redacted && keys.length > 0 ? (
        <>
          <dt>Before → after</dt>
          <dd>
            <ul className="m-0 list-none p-0 font-mono text-sm">
              {keys.map((k) => (
                <li key={k} className="mb-1">
                  <span className="text-muted">{k.replace(/_/g, " ")}:</span> {show(before[k])} → {show(after[k])}
                </li>
              ))}
            </ul>
          </dd>
        </>
      ) : null}
      <dt>Entry</dt>
      <dd className="font-mono text-sm text-muted">{row.id}</dd>
    </dl>
  );
}
